// PaymentMethod.jsx
// ─────────────────────────────────────────────────────────────────────────────
// "Payment Method" card on Edit Profile — lets a customer replace the card we
// bill without leaving the app and without talking to support.
//
// The new card is collected by Stripe Elements against a SetupIntent, so the
// raw PAN never touches our servers and 3DS/SCA is handled inside the iframe.
// Once Stripe confirms it, we promote it to default (customer default AND every
// live subscription) in one backend call, so the very next renewal uses it.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { stripePromise } from "../../lib/stripeClient";

import {
  getPaymentMethods,
  createPaymentMethodSetupIntent,
  setDefaultPaymentMethod,
  deletePaymentMethod,
  updatePaymentMethodBilling,
} from "../../Store/apiStore";
import styles from "./PaymentMethod.module.css";

// loadStripe MUST run at module scope — calling it inside the component would
// re-download Stripe.js on every render. Same env key the checkout sheet uses;
// never hardcoded, so test and live can't cross over.
// const PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";
// const stripePromise = PUBLISHABLE_KEY ? loadStripe(PUBLISHABLE_KEY) : null;

const ELEMENTS_APPEARANCE = {
  theme: "stripe",
  variables: {
    colorPrimary: "#7a5af8",
    colorText: "#24252c",
    colorDanger: "#d92d20",
    fontFamily: "Inter, system-ui, sans-serif",
    borderRadius: "10px",
    spacingUnit: "4px",
  },
};

const BRAND_LABELS = {
  visa: "Visa",
  mastercard: "Mastercard",
  amex: "American Express",
  discover: "Discover",
  diners: "Diners Club",
  jcb: "JCB",
  unionpay: "UnionPay",
  cartes_bancaires: "Cartes Bancaires",
};

const brandLabel = (brand) =>
  BRAND_LABELS[brand] || (brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : "Card");

const formatExpiry = (month, year) => {
  if (!month || !year) return null;
  return `${String(month).padStart(2, "0")}/${String(year).slice(-2)}`;
};

// A card is "expired" the moment its expiry month has passed — renewals on it
// will hard-decline, so it's worth calling out before the user finds out from a
// failed invoice.
const isExpired = (month, year) => {
  if (!month || !year) return false;
  const now = new Date();
  return year < now.getFullYear() || (year === now.getFullYear() && month < now.getMonth() + 1);
};

// The query params Stripe appends when it sends the customer back from a
// redirect-based 3DS challenge. We strip them off both the return_url we hand
// Stripe and the address bar afterwards, so a refresh can't replay an old
// confirmation.
const SETUP_RETURN_PARAMS = ["setup_intent", "setup_intent_client_secret", "redirect_status"];

const stripSetupParams = (href) => {
  const url = new URL(href);
  SETUP_RETURN_PARAMS.forEach((key) => url.searchParams.delete(key));
  return url.toString();
};

const CardGlyph = () => (
  <svg className={styles.cardGlyph} viewBox="0 0 32 22" aria-hidden="true">
    <rect x="0.75" y="0.75" width="30.5" height="20.5" rx="3.25" fill="#efe9fe" stroke="#dcd2fb" />
    <rect x="0.75" y="5" width="30.5" height="4" fill="#7a5af8" />
    <rect x="4" y="13" width="9" height="2.5" rx="1.25" fill="#b6a4fb" />
  </svg>
);

// Brand mark for the card row — Figma shows the network logo (VISA / Mastercard
// / Amex). Falls back to the generic glyph for other brands.
const BrandMark = ({ brand }) => {
  const b = String(brand || "").toLowerCase();
  if (b === "visa") return <span className={`${styles.brandMark} ${styles.brandVisa}`}>VISA</span>;
  if (b === "mastercard") return <span className={`${styles.brandMark} ${styles.brandMc}`}><i /><b /></span>;
  if (b === "amex") return <span className={`${styles.brandMark} ${styles.brandAmex}`}>AMEX</span>;
  return <CardGlyph />;
};

// ── The Elements-hosted "add a card" form ───────────────────────────────────
// Lives inside <Elements> because useStripe/useElements only resolve there.
// Exported for the Pounce plan screen, which needs the same form with its own
// wording; the defaults keep this screen exactly as it was.
export const AddCardForm = ({
  onCancel,
  onSaved,
  note = "Your card is saved securely by Stripe and becomes the card we bill for renewals.",
  submitLabel = "Save card",
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [ready, setReady] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || submitting) return;

    setSubmitting(true);
    setFormError("");

    // Most banks show the 3DS / OTP challenge in a Stripe modal right on top of
    // this page, which `if_required` handles without ever navigating away.
    // A few insist on a full-page redirect instead — and Stripe refuses to
    // confirm AT ALL if there's no return_url to come back to, even under
    // `if_required`. So always supply one; stripSetupParams keeps a previous
    // return from being baked into the next one.
    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: stripSetupParams(window.location.href) },
      redirect: "if_required",
    });

    if (error) {
      setFormError(error.message || "We couldn't save that card. Please check the details.");
      setSubmitting(false);
      return;
    }

    if (setupIntent?.status === "succeeded" && setupIntent?.payment_method) {
      await onSaved(setupIntent.payment_method);
      setSubmitting(false);
      return;
    }

    setFormError("Your bank is still verifying this card. Please try again in a moment.");
    setSubmitting(false);
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <PaymentElement
        onReady={() => setReady(true)}
        options={{
          layout: "tabs",
          wallets: { applePay: "never", googlePay: "never" },
          // Collect the full billing address (line1/line2, city, state, postal,
          // country) in the popup. 'auto' collects the whole address — NOT
          // "full" (that's Address-Element-only and crashes the PaymentElement).
          // Name/email are NOT collected here: Stripe's card PaymentElement can't
          // render an email field at all, so instead we COPY the default card's
          // name/email/address onto the new card after it's saved (handleSaved),
          // guaranteeing the new default card carries the same billing details.
          fields: { billingDetails: { address: "auto" } },
        }}
      />

      {formError && <p className={styles.formError}>{formError}</p>}

      <p className={styles.formNote}>{note}</p>

      <div className={styles.formActions}>
        <button
          type="button"
          className={styles.secondaryBtn}
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className={styles.primaryBtn}
          disabled={!stripe || !ready || submitting}
        >
          {submitting ? "Saving…" : submitLabel}
        </button>
      </div>
    </form>
  );
};

// `onDefaultChange` is fired whenever the default card changes (set-default,
// remove-that-repoints-default, or a newly-saved card promoted to default) so a
// parent — e.g. the Billing Info block — can refresh the details it shows for
// the default card.
const PaymentMethod = ({ onDefaultChange } = {}) => {
  const [cards, setCards] = useState([]);
  const [defaultId, setDefaultId] = useState(null);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [clientSecret, setClientSecret] = useState("");
  const [startingSetup, setStartingSetup] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [confirmRemoveId, setConfirmRemoveId] = useState(null);
  const [menuOpenId, setMenuOpenId] = useState(null); // which card's ⋮ menu is open

  /*
   * ⋮ menu scroll, bahar tap ya Escape pe band. Khula rehta to page scroll
   * hone pe wo My Account ke fixed header (z-index 1) ke upar chadh jaata —
   * menu ka z-index 30 hai. Scroll capture phase me suna: scroll window ka
   * ho ya kisi andar ke container ka, dono pakde jaate hain.
   */
  const menuWrapRef = useRef(null);
  useEffect(() => {
    if (!menuOpenId) return undefined;
    const close = () => setMenuOpenId(null);
    const onPointer = (e) => {
      if (!menuWrapRef.current?.contains(e.target)) close();
    };
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("scroll", close, true);
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", close, true);
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpenId]);

  // "Remove this card?" bhi wahi absolute z-index 30 popup hai — scroll pe
  // band. Remove chal raha ho (busyId) tab nahi, warna "Removing…" beech me
  // gayab ho jaata.
  useEffect(() => {
    if (!confirmRemoveId || busyId) return undefined;
    const close = () => setConfirmRemoveId(null);
    window.addEventListener("scroll", close, true);
    return () => window.removeEventListener("scroll", close, true);
  }, [confirmRemoveId, busyId]);

  // The add-card form renders below the (scrollable) card list, so it can open
  // off-screen. Scroll it into view when it appears so the user sees the fields.
  const formRef = useRef(null);
  useEffect(() => {
    if (clientSecret && formRef.current) {
      // Next tick, after the Elements form mounts, bring it into view.
      const t = setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [clientSecret]);

  // Auto-dismiss the success notice ("Card removed." / "Payment method updated.")
  // after a few seconds — it's a transient confirmation, not a permanent banner.
  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(""), 3000);
    return () => clearTimeout(t);
  }, [notice]);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const data = await getPaymentMethods();
      setCards(data?.paymentMethods || []);
      setDefaultId(data?.defaultPaymentMethodId || null);
      setHasActiveSubscription(!!data?.hasActiveSubscription);
      setError("");
    } catch (err) {
      setError(err.message || "Failed to load your saved cards");
    } finally {
      setLoading(false);
    }
  }, []);

  // Coming back from a redirect-based 3DS challenge, the card is already saved
  // on the Stripe customer but nothing has promoted it yet — the component that
  // would have done so was torn down by the redirect. Finish the job here,
  // otherwise the user sees their new card sitting inactive while the old one
  // keeps getting billed.
  const resumeFromRedirect = useCallback(async () => {
    const secret = new URLSearchParams(window.location.search).get(
      "setup_intent_client_secret"
    );
    if (!secret) return;

    // Scrub the URL first: whatever happens below, a refresh must not replay it.
    window.history.replaceState({}, "", stripSetupParams(window.location.href));

    try {
      const stripe = await stripePromise;
      const { setupIntent } = await stripe.retrieveSetupIntent(secret);

      if (setupIntent?.status === "succeeded" && setupIntent?.payment_method) {
        await setDefaultPaymentMethod(setupIntent.payment_method);
        setNotice("Payment method updated. Future charges will use this card.");
        return;
      }
      setError("Your bank didn't finish verifying that card. Please try again.");
    } catch (err) {
      setError(err.message || "We couldn't confirm your new card. Please try again.");
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await resumeFromRedirect();
      if (!cancelled) await load();
    })();
    return () => {
      cancelled = true;
    };
  }, [resumeFromRedirect, load]);

  const openAddCard = async () => {
    setNotice("");
    setError("");
    setStartingSetup(true);
    try {
      const { clientSecret: secret } = await createPaymentMethodSetupIntent();
      if (!secret) throw new Error("Could not start card setup. Please try again.");
      setClientSecret(secret);
    } catch (err) {
      setError(err.message || "Could not start card setup. Please try again.");
    } finally {
      setStartingSetup(false);
    }
  };

  // A card saved from this screen is one the user wants USED — promote it in
  // the same breath, otherwise it would sit there while the old card keeps
  // getting charged, which is exactly the confusion this feature exists to fix.
  const handleSaved = async (paymentMethodId) => {
    try {
      // Copy the cardholder identity (name / email / address) from the CURRENT
      // DEFAULT card onto the NEW card, so the new default shows the SAME
      // billing details. Stripe's card form can't collect an email field at
      // all, so without this the new card would have blank name/email (QA bug:
      // Billing Info showed "—" after adding a card). billing_details are
      // per-card in Stripe, so this is a one-time COPY — editing one card later
      // still only changes that card.
      //
      // IMPORTANT: fetch a FRESH list here (not the stale `cards` state) and
      // copy ONLY from the real default card (matched by defaultPaymentMethodId).
      // The old code fell back to "any card with a name", which grabbed an OLD
      // card's details (e.g. "kajal") instead of the current default's (e.g.
      // "sharmakajal") — so the new card showed the wrong name.
      try {
        const fresh = await getPaymentMethods();
        const freshCards = fresh?.paymentMethods || [];
        const freshDefaultId = fresh?.defaultPaymentMethodId;
        const src =
          freshCards.find((c) => c.id === freshDefaultId) ||
          freshCards.find((c) => c.isDefault) ||
          null;
        if (src && (src.name || src.email || src.address?.line1)) {
          await updatePaymentMethodBilling(paymentMethodId, {
            name: src.name || "",
            email: src.email || "",
            address: {
              line1: src.address?.line1 || "",
              line2: src.address?.line2 || "",
              city: src.address?.city || "",
              state: src.address?.state || "",
              postalCode: src.address?.postalCode || "",
              country: src.address?.country || "",
            },
          });
        }
      } catch (e) {
        // Non-fatal: the card is still saved and usable; it just won't carry
        // the copied name/email. Don't block making it the default.
        console.error("[PaymentMethod] copy billing details failed:", e?.message);
      }

      await setDefaultPaymentMethod(paymentMethodId);
      setNotice("Payment method updated. Future charges will use this card.");
      onDefaultChange?.(); // new card became default → refresh billing block
    } catch (err) {
      setNotice("");
      setError(err.message || "Card saved, but we couldn't make it your default.");
    }
    setClientSecret("");
    await load({ silent: true });
  };

  const handleMakeDefault = async (paymentMethodId) => {
    setBusyId(paymentMethodId);
    setMenuOpenId(null);
    setNotice("");
    setError("");
    try {
      await setDefaultPaymentMethod(paymentMethodId);
      setNotice("Payment method updated. Future charges will use this card.");
      await load({ silent: true });
      onDefaultChange?.(); // default changed → refresh billing block
    } catch (err) {
      setError(err.message || "Failed to update your default card");
    } finally {
      setBusyId(null);
    }
  };

  const handleRemove = async (paymentMethodId) => {
    setBusyId(paymentMethodId);
    setNotice("");
    setError("");
    try {
      const res = await deletePaymentMethod(paymentMethodId);
      setConfirmRemoveId(null);
      setMenuOpenId(null);
      setNotice("Card removed.");
      await load({ silent: true });
      // Removing the default card re-points the default to another card
      // server-side (deletePaymentMethod returns the new defaultPaymentMethodId),
      // so refresh the billing block to show the new default's details.
      if (res && Object.prototype.hasOwnProperty.call(res, "defaultPaymentMethodId")) {
        onDefaultChange?.();
      }
    } catch (err) {
      setError(err.message || "Failed to remove this card");
    } finally {
      setBusyId(null);
    }
  };

  const elementsOptions = useMemo(
    () => ({ clientSecret, appearance: ELEMENTS_APPEARANCE }),
    [clientSecret]
  );

  // No pubblishable key means Stripe.js can't load at all. Say so plainly rather
  // than rendering an "Add card" button that silently does nothing.
  if (!stripePromise) {
    return (
      <div className={styles.container}>
        <h3 className={styles.title}>Payment Method</h3>
        <p className={styles.errorText}>
          Card management is unavailable right now. Please contact support to update your card.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        <div>
          <h3 className={styles.title}>Payment Methods</h3>
          <p className={styles.subtitle}>The card we charge for your plan and renewals.</p>
        </div>
        {!clientSecret && !loading && (
          <button className={styles.addBtn} onClick={openAddCard} disabled={startingSetup}>
            {startingSetup ? "Opening…" : "Add new"}
          </button>
        )}
      </div>

      {/* Success notice — chhota toast, kuch second baad khud chhup jaata hai. */}
      {notice && (
        <p className={styles.noticeToast} role="status" aria-live="polite">
          {notice}
        </p>
      )}
      {error && <p className={styles.errorText}>{error}</p>}

      {loading ? (
        <div className={styles.skeletonRow}>
          <span className={styles.skeletonBlock} />
          <span className={styles.skeletonBlockSm} />
        </div>
      ) : (
        <>
          {cards.length === 0 && !clientSecret && (
            <p className={styles.emptyText}>
              No card saved yet. Add one so your plan renews without interruption.
            </p>
          )}

          {/* Scroll container — shows ~5 cards, then scrolls. When a ⋮ menu (or a
              remove-confirm) is open, switch overflow to visible so the popup
              isn't clipped by the scroll box — otherwise it got cut off and the
              user had to scroll to see "Set as default / Remove". */}
          <div className={`${styles.pmScroll} ${(menuOpenId || confirmRemoveId) ? styles.pmScrollOpen : ''}`}>
          {cards.map((card) => {
            const expired = isExpired(card.expMonth, card.expYear);
            const expiry = formatExpiry(card.expMonth, card.expYear);
            const isBusy = busyId === card.id;
            const isDefault = card.id === defaultId;
            // The backend refuses to detach the last card while a plan is live
            // (it would set the next renewal up to fail). Same guard as
            // deletePaymentMethod — offer nothing that can only ever error.
            const isLockedLastCard = cards.length <= 1 && hasActiveSubscription;
            const menuOpen = menuOpenId === card.id;

            return (
              <div key={card.id} className={styles.pmRow}>
                <BrandMark brand={card.brand} />

                <div className={styles.pmInfo}>
                  <span className={styles.pmBrand}>{brandLabel(card.brand)}{card.funding === "credit" ? " card" : ""}</span>
                  <span className={expired ? styles.expiredText : styles.pmDots}>
                    •••• {card.last4}{expiry ? `  ·  ${expired ? "Expired" : "Expires"} ${expiry}` : ""}
                  </span>
                </div>

                {/* Default → badge. Others → ⋮ menu (Set default / Remove). */}
                {isDefault ? (
                  <span className={styles.defaultBadge}>Default</span>
                ) : (
                  <div className={styles.pmMenuWrap} ref={menuOpen ? menuWrapRef : undefined}>
                    <button
                      type="button"
                      className={styles.pmDotsBtn}
                      aria-label={`Actions for card ending ${card.last4}`}
                      aria-haspopup="menu"
                      aria-expanded={menuOpen}
                      disabled={isBusy}
                      onClick={() => setMenuOpenId(menuOpen ? null : card.id)}
                    >
                      {isBusy ? "…" : "⋮"}
                    </button>

                    {menuOpen && (
                      <div className={styles.pmMenu} role="menu">
                        <button
                          type="button"
                          role="menuitem"
                          className={styles.pmMenuItem}
                          onClick={() => handleMakeDefault(card.id)}
                        >
                          Set as default
                        </button>
                        {isLockedLastCard ? (
                          <span className={styles.pmMenuNote}>Add another card to remove this one</span>
                        ) : (
                          <button
                            type="button"
                            role="menuitem"
                            className={`${styles.pmMenuItem} ${styles.pmMenuDanger}`}
                            onClick={() => {
                              setMenuOpenId(null);
                              setError("");
                              setNotice("");
                              setConfirmRemoveId(card.id);
                            }}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Inline remove confirmation (keeps the destructive action gated). */}
                {confirmRemoveId === card.id && (
                  <div className={styles.pmConfirm}>
                    <span>Remove this card?</span>
                    <button className={styles.dangerBtn} onClick={() => handleRemove(card.id)} disabled={isBusy}>
                      {isBusy ? "Removing…" : "Remove"}
                    </button>
                    <button className={styles.linkBtn} onClick={() => setConfirmRemoveId(null)} disabled={isBusy}>
                      Keep
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          </div>

          {clientSecret && (
            <div className={styles.formWrapper} ref={formRef}>
              <Elements stripe={stripePromise} options={elementsOptions}>
                <AddCardForm
                  onCancel={() => setClientSecret("")}
                  onSaved={handleSaved}
                />
              </Elements>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PaymentMethod;
